import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Grid,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  Download,
  Visibility as Eye,
  Block,
  CheckCircle,
  Info,
  Lock,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { fetchUsers, updateUserStatus } from '../../store/slices/adminSlice';

// Mock data for users with realistic Nigerian names

type UserStatus = 'active' | 'suspended' | 'unverified';
type KYCStatus = 'verified' | 'pending' | 'rejected';

interface UserWithMock {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email: string;
  phoneNumber?: string;
  status: UserStatus;
  kycStatus?: KYCStatus;
  balance?: number;
  createdAt: string;
  address?: string;
}

const UserManagementPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { users, usersLoading } = useSelector((state: RootState) => state.admin);

  // Local state
  const [displayedUsers, setDisplayedUsers] = useState<UserWithMock[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedUser, setSelectedUser] = useState<UserWithMock | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Fetch users on mount
  useEffect(() => {
    dispatch(fetchUsers({ page: 1, limit: 50 }));
  }, [dispatch]);

  // Use API users - data now includes kycStatus and balance from backend
  useEffect(() => {
    if (users && users.length > 0) {
      setDisplayedUsers(users as UserWithMock[]);
    }
  }, [users]);

  // Filter and search logic
  const filteredUsers = displayedUsers.filter((user) => {
    const matchesSearch =
      (user.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.phoneNumber || '').includes(searchQuery);

    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesKyc = kycFilter === 'all' || (user.kycStatus === kycFilter);

    return matchesSearch && matchesStatus && matchesKyc;
  });

  // Pagination
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (user: UserWithMock) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSuspendToggle = (userId: string, currentStatus: UserStatus) => {
    const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
    dispatch(updateUserStatus({ id: userId, status: newStatus }));

    // Update local display
    setDisplayedUsers((prev) =>
      prev.map((user) =>
        user.id === userId ? { ...user, status: newStatus } : user
      )
    );

    if (selectedUser?.id === userId) {
      setSelectedUser((prev) =>
        prev ? { ...prev, status: newStatus } : null
      );
    }
  };

  const handleExport = () => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Status', 'KYC Status', 'Balance', 'Joined'],
      ...filteredUsers.map((user) => [
        user.name || `${user.firstName} ${user.lastName}`,
        user.email,
        user.phoneNumber || 'N/A',
        user.status,
        user.kycStatus || 'N/A',
        user.balance || 0,
        user.createdAt,
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const getStatusColor = (status: UserStatus) => {
    switch (status) {
      case 'active':
        return { bg: 'rgba(0, 196, 140, 0.1)', color: '#00C48C' };
      case 'suspended':
        return { bg: 'rgba(255, 71, 87, 0.1)', color: '#FF4757' };
      case 'unverified':
        return { bg: 'rgba(255, 185, 70, 0.1)', color: '#FFB946' };
      default:
        return { bg: 'rgba(127, 131, 156, 0.1)', color: '#7F839C' };
    }
  };

  const getKycColor = (kycStatus?: KYCStatus) => {
    switch (kycStatus) {
      case 'verified':
        return { bg: 'rgba(0, 196, 140, 0.1)', color: '#00C48C' };
      case 'pending':
        return { bg: 'rgba(255, 185, 70, 0.1)', color: '#FFB946' };
      case 'rejected':
        return { bg: 'rgba(255, 71, 87, 0.1)', color: '#FF4757' };
      default:
        return { bg: 'rgba(127, 131, 156, 0.1)', color: '#7F839C' };
    }
  };

  const getInitials = (user: UserWithMock) => {
    const firstName = user.firstName || user.name?.split(' ')[0] || 'U';
    const lastName = user.lastName || user.name?.split(' ')[1] || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getAvatarColor = (id: string) => {
    const colors = ['#11C1EC', '#09486D', '#00C48C', '#FFB946', '#FF4757'];
    return colors[parseInt(id) % colors.length];
  };

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', pb: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#202647', mb: 1 }}>
            User Management
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${filteredUsers.length} users`}
              sx={{
                bgcolor: 'rgba(17, 193, 236, 0.1)',
                color: '#09486D',
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleExport}
          sx={{
            background: 'linear-gradient(135deg, #11C1EC 0%, #09486D 100%)',
            color: 'white',
            borderRadius: 25,
            padding: '10px 28px',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          Export CSV
        </Button>
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 3, borderRadius: 4, border: 'none' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* Search Field */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Search by name, email, phone"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#11C1EC', mr: 1 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            {/* Status Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="unverified">Unverified</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* KYC Status Filter */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>KYC Status</InputLabel>
                <Select
                  value={kycFilter}
                  label="KYC Status"
                  onChange={(e) => {
                    setKycFilter(e.target.value);
                    setPage(0);
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All KYC</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date Range (Optional) */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Users Table Card */}
      <Card sx={{ borderRadius: 4, border: 'none', overflow: 'hidden' }}>
        {usersLoading && paginatedUsers.length === 0 ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(17, 193, 236, 0.05)' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>KYC</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }} align="right">
                      Balance
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Joined</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: '#7F839C' }}>
                          No users found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedUsers.map((user, index) => (
                      <TableRow
                        key={user.id}
                        sx={{
                          '&:hover': { bgcolor: 'rgba(17, 193, 236, 0.02)' },
                          borderBottom: index < paginatedUsers.length - 1 ? '1px solid #E8E8F0' : 'none',
                        }}
                      >
                        {/* Name with Avatar */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                bgcolor: getAvatarColor(user.id),
                                color: 'white',
                                fontWeight: 700,
                                width: 36,
                                height: 36,
                              }}
                            >
                              {getInitials(user)}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: '#202647',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {user.name || `${user.firstName} ${user.lastName}`}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Email */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#7F839C',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 200,
                            }}
                          >
                            {user.email}
                          </Typography>
                        </TableCell>

                        {/* Phone */}
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#7F839C' }}>
                            {user.phoneNumber || 'N/A'}
                          </Typography>
                        </TableCell>

                        {/* Status Chip */}
                        <TableCell>
                          <Chip
                            label={user.status}
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(user.status).bg,
                              color: getStatusColor(user.status).color,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>

                        {/* KYC Status Chip */}
                        <TableCell>
                          <Chip
                            label={user.kycStatus || 'N/A'}
                            size="small"
                            sx={{
                              bgcolor: getKycColor(user.kycStatus as KYCStatus).bg,
                              color: getKycColor(user.kycStatus as KYCStatus).color,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'capitalize',
                            }}
                          />
                        </TableCell>

                        {/* Balance */}
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: '#202647',
                            }}
                          >
                            ₦{(user.balance || 0).toLocaleString('en-NG')}
                          </Typography>
                        </TableCell>

                        {/* Joined Date */}
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#7F839C' }}>
                            {new Date(user.createdAt).toLocaleDateString('en-NG', {
                              year: '2-digit',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </Typography>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(user)}
                              sx={{
                                color: '#11C1EC',
                                '&:hover': { bgcolor: 'rgba(17, 193, 236, 0.1)' },
                              }}
                              title="View Details"
                            >
                              <Eye sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleSuspendToggle(user.id, user.status)}
                              sx={{
                                color: user.status === 'suspended' ? '#00C48C' : '#FF4757',
                                '&:hover': {
                                  bgcolor:
                                    user.status === 'suspended'
                                      ? 'rgba(0, 196, 140, 0.1)'
                                      : 'rgba(255, 71, 87, 0.1)',
                                },
                              }}
                              title={user.status === 'suspended' ? 'Activate' : 'Suspend'}
                            >
                              {user.status === 'suspended' ? (
                                <CheckCircle sx={{ fontSize: 18 }} />
                              ) : (
                                <Block sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                bgcolor: 'rgba(17, 193, 236, 0.02)',
                borderTop: '1px solid #E8E8F0',
                '& .MuiTablePagination-root': {
                  color: '#7F839C',
                },
              }}
            />
          </>
        )}
      </Card>

      {/* User Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#202647' }}>User Details</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          {selectedUser && (
            <Box>
              {/* User Profile */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: getAvatarColor(selectedUser.id),
                    color: 'white',
                    fontWeight: 700,
                    width: 56,
                    height: 56,
                    fontSize: '1.5rem',
                  }}
                >
                  {getInitials(selectedUser)}
                </Avatar>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: '#202647' }}
                  >
                    {selectedUser.name || `${selectedUser.firstName} ${selectedUser.lastName}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7F839C' }}>
                    {selectedUser.email}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Contact Info */}
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#202647', mb: 1.5 }}
              >
                Contact Information
              </Typography>
              <Box sx={{ mb: 2, bgcolor: 'rgba(17, 193, 236, 0.05)', p: 2, borderRadius: 2 }}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#7F839C', mb: 0.5 }}>
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#202647', fontWeight: 600 }}>
                    {selectedUser.email}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#7F839C', mb: 0.5 }}>
                    Phone
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#202647', fontWeight: 600 }}>
                    {selectedUser.phoneNumber || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: '#7F839C', mb: 0.5 }}>
                    Address
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#202647', fontWeight: 600 }}>
                    {selectedUser.address || 'Not provided'}
                  </Typography>
                </Box>
              </Box>

              {/* Account Information */}
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#202647', mb: 1.5 }}
              >
                Account Information
              </Typography>
              <Box sx={{ mb: 2, bgcolor: 'rgba(17, 193, 236, 0.05)', p: 2, borderRadius: 2 }}>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#7F839C', mb: 0.5 }}>
                    Balance
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#202647', fontWeight: 700 }}>
                    ₦{(selectedUser.balance || 0).toLocaleString('en-NG')}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#7F839C', mb: 0.5 }}>
                    Status
                  </Typography>
                  <Chip
                    label={selectedUser.status}
                    size="small"
                    sx={{
                      bgcolor: getStatusColor(selectedUser.status).bg,
                      color: getStatusColor(selectedUser.status).color,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#7F839C', mb: 0.5 }}>
                    KYC Status
                  </Typography>
                  <Chip
                    label={selectedUser.kycStatus || 'N/A'}
                    size="small"
                    sx={{
                      bgcolor: getKycColor(selectedUser.kycStatus as KYCStatus).bg,
                      color: getKycColor(selectedUser.kycStatus as KYCStatus).color,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: '#7F839C', mb: 0.5 }}>
                    Member Since
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#202647', fontWeight: 600 }}>
                    {new Date(selectedUser.createdAt).toLocaleDateString('en-NG', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>
                </Box>
              </Box>

              {/* Transaction History Placeholder */}
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#202647', mb: 1.5 }}
              >
                Recent Transactions
              </Typography>
              <Box sx={{ bgcolor: 'rgba(127, 131, 156, 0.05)', p: 2, borderRadius: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#7F839C' }}>
                  Last 5 transactions will appear here
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: '#7F839C',
              borderRadius: 25,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Close
          </Button>
          {selectedUser && (
            <>
              <Button
                onClick={() => {
                  // Reset password action
                  handleCloseDialog();
                }}
                startIcon={<Lock />}
                sx={{
                  color: '#FFB946',
                  borderRadius: 25,
                  textTransform: 'none',
                  fontWeight: 600,
                  border: '1px solid #FFB946',
                }}
              >
                Reset Password
              </Button>
              <Button
                onClick={() => {
                  handleSuspendToggle(selectedUser.id, selectedUser.status);
                  handleCloseDialog();
                }}
                variant="contained"
                sx={{
                  background:
                    selectedUser.status === 'suspended'
                      ? 'linear-gradient(135deg, #00C48C 0%, #00A06E 100%)'
                      : 'linear-gradient(135deg, #FF4757 0%, #E6303F 100%)',
                  borderRadius: 25,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {selectedUser.status === 'suspended' ? 'Activate' : 'Suspend'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementPage;
